import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";

/**
 * LAZY on purpose. A top-level import of anything from the Sequences page
 * pulls that page's whole module tree (sequenceStoreData,
 * InitialScheduleInfoPopup, MultiInboxSelection, toaster, paywall) into the
 * copilot bundle through BlockRenderer, and a failure anywhere in that tree
 * leaves BlockRenderer unloaded — the entire copilot renders nothing, with no
 * console error and a clean compile.
 *
 * ActivateToggle, NOT ActivateSequenceToggle: the wrapper's only job is to add
 * the product's CustomTooltip, which is sized for the Sequences page (too large
 * here) and renders in its own stacking context, so it hid behind the next
 * row's toggle. We take the inner toggle and supply our own tooltip.
 */
const ActivateToggle = lazy(() =>
  import("Pages/Dashboard/sequences/SequencePage/ActivateToggle")
);

/**
 * Pause/resume mirrors SequenceTable's own non-draft branch: the product only
 * uses ActivateSequenceToggle for DRAFT rows (it opens the initial-schedule
 * popup and can never switch back off). Every other row gets a plain checkbox
 * bound to the same store call, which is the only part of the page's 120-line
 * toggleAction that is not paywall/bulk-job handling.
 */
const isDraft = seq => (seq.status || "").toLowerCase() === "draft";

const isActive = seq =>
  !["paused", "suspended", "pause"].includes((seq.status || "").toLowerCase());

/**
 * Sequences, rendered the way the Sequences page renders them.
 *
 * Deliberately NOT entity cards: /v1/sequences carries no rates, so a card
 * could only ever show a name and a contact count — which is why asking the
 * copilot to list sequences looked nothing like the real page.
 *
 * Rows carry only what the agent supplies: which sequences to show, plus the
 * rate columns merged server-side from /v1/reports/sequences.
 *
 * KNOWN GAP: draft rows open the product's activate popup against this compact
 * row, which has no inboxIds/memberId/scheduleWindowId/steps, so the popup's
 * inbox and schedule sections stay on skeleton loaders. Fixing that needs the
 * full sequence fetched on click plus scheduleDataStore primed — the schedule
 * list is loaded by a hook keyed on the /sequences path, so it is always empty
 * on /copilot. Not done here.
 */

const SORTS = {
  name: s => (s.name || "").toLowerCase(),
  contacts: s => s.contacts || 0,
  openRate: s => (typeof s.openRate === "number" ? s.openRate : -1),
  clickRate: s => (typeof s.clickRate === "number" ? s.clickRate : -1),
  replyRate: s => (typeof s.replyRate === "number" ? s.replyRate : -1),
  created: s => {
    const t = new Date(s.created || 0).getTime();
    return Number.isNaN(t) ? 0 : t;
  }
};

const pct = value =>
  typeof value === "number" && Number.isFinite(value)
    ? `${Math.round(value * 10) / 10}%`
    : "—";

const count = value =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString("en-US")
    : "0";

const createdLabel = value => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
};

// Rows revealed per "Load more" click, and the initial window.
const PAGE = 10;

const SequenceListBlock = observer(({ block }) => {
  const rows = block.sequences || [];
  // Newest first matches the Sequences page — but only when the server sent no
  // opinion. A ranked list (e.g. "top performing sequence", ordered by replies
  // then opens) was being re-sorted by date here, which threw the ranking away
  // and put zero-activity sequences at the top of an answer about performance.
  const [sort, setSort] = useState(
    block.ranked ? { key: null, dir: "desc" } : { key: "created", dir: "desc" }
  );
  // How many rows are on screen. Deliberately NOT reset by sorting: re-sorting
  // 20 visible rows and getting 10 back reads as the list having lost rows.
  const [shown, setShown] = useState(PAGE);
  // Set while a server page is in flight, so its rows are revealed on arrival
  // instead of making the user click the same button a second time.
  const awaitingPage = useRef(false);
  // Row-level spinner id while the agent fetches the sequence for the panel.
  const [opening, setOpening] = useState("");
  const [hovered, setHovered] = useState("");
  const [busy, setBusy] = useState("");
  // Optimistic status overrides, so a toggled row updates without the agent
  // having to re-emit the whole block.
  const [statuses, setStatuses] = useState({});

  const togglePause = async seq => {
    if (busy) return;
    const active = isActive(seq);
    setBusy(seq.id);
    try {
      // Lazy: the sequences store pulls in the page's data tree, and this block
      // must never make the copilot bundle depend on it at module scope.
      const { sequenceStoreData } = await import("Stores/sequenceData");
      await sequenceStoreData.getBackgroundPauseResumeSequence({
        filter: { sequenceId: seq.id },
        type: active ? "PAUSE" : "RESUME",
        totalCount: seq.contacts || 0
      });
      setStatuses(prev => ({ ...prev, [seq.id]: active ? "PAUSED" : null }));
    } finally {
      setBusy("");
    }
  };
  // Rows from a requested page arrived: show them.
  useEffect(() => {
    if (!awaitingPage.current) return;
    if (rows.length > shown) {
      awaitingPage.current = false;
      setShown(Math.min(shown + PAGE, rows.length));
    }
  }, [rows.length, shown]);

  const sorted = useMemo(() => {
    // No key = keep the server's order verbatim. Local status overrides (the
    // activate toggle) still apply — only the ordering is left alone.
    if (!sort.key) {
      return rows.map(r => (r.id in statuses ? { ...r, status: statuses[r.id] } : r));
    }
    const read = SORTS[sort.key] || SORTS.created;
    const factor = sort.dir === "asc" ? 1 : -1;
    return rows
      .map(r => (r.id in statuses ? { ...r, status: statuses[r.id] } : r))
      .sort((a, b) => {
        const av = read(a);
        const bv = read(b);
        if (av === bv) return 0;
        return av > bv ? factor : -factor;
      });
  }, [rows, sort, statuses]);

  if (!rows.length) return null;

  // What the account holds for this list's filters, not what arrived so far.
  // `exhausted` wins: a stale total would leave the CTA offering rows the
  // server has already said do not exist.
  const total =
    block.exhausted || typeof block.total !== "number"
      ? rows.length
      : Math.max(block.total, rows.length);
  const visible = sorted.slice(0, shown);
  const remaining = total - visible.length;

  // Reveal what we already hold; only ask the agent once the local rows run out.
  const loadMore = () => {
    if (shown < sorted.length) {
      setShown(Math.min(shown + PAGE, sorted.length));
      return;
    }
    awaitingPage.current = true;
    copilotStore.loadMoreSequences(block.id);
  };

  const toggleSort = key =>
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "name" ? "asc" : "desc" }
    );

  // Straight to /cm/seq/{id} — no agent round trip, so this works while the
  // agent is streaming and on a thread reopened after a refresh, and the panel
  // gets the full stored steps rather than the tool's trimmed shape.
  const open = seq => {
    setOpening(seq.id);
    copilotStore
      .openSequenceDirect(seq.id, seq.name)
      .finally(() => setOpening(""));
  };

  const Header = ({ label, sortKey, className }) => (
    <button
      type="button"
      className={`${className} cpSeqList__sortable${
        sort.key === sortKey ? " is-active" : ""
      }`}
      onClick={() => toggleSort(sortKey)}
    >
      {label}
      {sort.key === sortKey && (
        <i className="material-icons-outlined cpSeqList__sortIcon">
          {sort.dir === "asc" ? "arrow_upward" : "arrow_downward"}
        </i>
      )}
    </button>
  );

  return (
    <div className="cpVisual cpSeqList">
      {block.title && <div className="cpVisual__title">{block.title}</div>}

      <div className="cpSeqList__head">
        {/* Empty cell over the toggle column — without it every header shifted
            one column left and "Name" was clipped into the 34px toggle slot. */}
        <span />
        <Header label="Name" sortKey="name" className="cpSeqList__cName" />
        <Header label="Contacts" sortKey="contacts" className="cpSeqList__cNum" />
        <Header label="Open" sortKey="openRate" className="cpSeqList__cNum" />
        <Header label="Click" sortKey="clickRate" className="cpSeqList__cNum" />
        <Header label="Reply" sortKey="replyRate" className="cpSeqList__cNum" />
        <Header label="Created" sortKey="created" className="cpSeqList__cDate" />
        <span />
      </div>

      <ol className="cpSeqList__rows">
        {visible.map(seq => (
          <li className="cpSeqList__row" key={seq.id}>
            <div
              className="cpSeqList__toggle"
              onClick={e => e.stopPropagation()}
            >
              <div
                className="toogleSequence"
                onMouseEnter={() => setHovered(seq.id)}
                onMouseLeave={() => setHovered("")}
              >
                {isDraft(seq) ? (
                  <Suspense
                    fallback={<div className="cpSeqList__togglePlaceholder" />}
                  >
                    <ActivateToggle
                      status={seq.status}
                      seqData={seq}
                      setShowTooltip={() => {}}
                      getSelectedSeqData={() => {}}
                    />
                  </Suspense>
                ) : (
                  <div
                    className={`toogleButton${busy === seq.id ? " loading" : ""}`}
                  >
                    <input
                      type="checkbox"
                      className="toggleCheckbox"
                      checked={isActive(seq)}
                      onChange={() => togglePause(seq)}
                    />
                    <div className="knobs" />
                    <div className="layer" />
                  </div>
                )}
                {hovered === seq.id && (
                  <span className="cpSeqList__tip">
                    {isDraft(seq)
                      ? "Activate sequence"
                      : isActive(seq)
                      ? "Pause sequence"
                      : "Resume sequence"}
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              className="cpSeqList__cName"
              onClick={() => open(seq)}
              title={seq.name}
            >
              <span className="cpSeqList__nameText">{seq.name}</span>
              {seq.folderName && (
                <span className="cpSeqList__folder">{seq.folderName}</span>
              )}
            </button>

            <span className="cpSeqList__cNum">{count(seq.contacts)}</span>
            <span className="cpSeqList__cNum cpSeqList__open">{pct(seq.openRate)}</span>
            <span className="cpSeqList__cNum cpSeqList__click">{pct(seq.clickRate)}</span>
            <span className="cpSeqList__cNum cpSeqList__reply">{pct(seq.replyRate)}</span>
            <span className="cpSeqList__cDate">{createdLabel(seq.created)}</span>

            <button
              type="button"
              className="cpSeqList__cGo"
              aria-label={`View ${seq.name}`}
              onClick={() => open(seq)}
            >
              {opening === seq.id ? (
                <span className="cpSeqList__spinner" />
              ) : (
                <i className="material-icons-outlined">chevron_right</i>
              )}
            </button>
          </li>
        ))}
      </ol>

      {remaining > 0 && (
        <button
          type="button"
          className="cpSeqList__more"
          onClick={loadMore}
          disabled={block.loading}
        >
          {block.loading ? "Loading…" : `Load ${remaining} more`}
        </button>
      )}
      {block.loadError && (
        <div className="cpSeqList__moreError">
          Could not load more sequences. Try again.
        </div>
      )}
    </div>
  );
});

export default SequenceListBlock;
